import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { login, register } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Phone, User, Lock, Users, Eye, EyeOff, KeyRound, Mail, Tag, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { getStoredPromo } from '../lib/promoLink';
import { captureRefFromUrl, getStoredRefCode } from '../lib/refLink';

/** Capture & remember the UTM/ref source from the URL so we can attribute conversions. */
const SOURCE_KEY = 'ds_signup_source_v1';
function captureSignupSource() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw =
      params.get('utm_source') ||
      params.get('utm_medium') ||
      params.get('ref') ||
      params.get('src') ||
      params.get('via') ||
      null;
    if (raw) {
      const campaign = params.get('utm_campaign');
      const value = campaign ? `${raw}:${campaign}` : raw;
      window.localStorage.setItem(SOURCE_KEY, value.slice(0, 80));
    }
  } catch { /* ignore */ }
}
function getStoredSignupSource() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(SOURCE_KEY) || null;
  } catch { return null; }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intendedRole = searchParams.get('role') || 'coach';
  const { setAuth, isAuthenticated, registeredAs, user: authUser, teamsAsCoach } = useAuthStore();
  
  // Auto-redirect if already logged in. Brand-new coach accounts (no teams yet,
  // registered as coach) get the 60-second onboarding wizard instead of the
  // dashboard. Everyone else goes to their home surface.
  useEffect(() => {
    if (isAuthenticated) {
      if (intendedRole === 'parent' && registeredAs === 'parent') {
        navigate('/parent-browse', { replace: true });
        return;
      }
      const looksLikeNewCoach = registeredAs === 'coach'
        && (!teamsAsCoach || teamsAsCoach.length === 0)
        && (!authUser?.teams_as_coach || authUser.teams_as_coach.length === 0);
      if (looksLikeNewCoach) {
        navigate('/onboarding', { replace: true });
        return;
      }
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated]);
  
  const [loginData, setLoginData] = useState(() => {
    const saved = localStorage.getItem('dmp-remember-username');
    return { username: saved || '', password: '' };
  });
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('dmp-remember-username'));
  const [registerData, setRegisterData] = useState({ username: '', password: '', phone: '', email: '', discountCode: '' });
  const [discountStatus, setDiscountStatus] = useState(null); // {ok: bool, summary?: string}
  const [recoveryInfo, setRecoveryInfo] = useState(null); // {field, existingUsername, hasSubscription, suggestedPromo} when phone/email conflict
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' — controlled so recovery card can switch
  const [loading, setLoading] = useState(false);
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  // Auto-prefill any captured promo code (from /?promo=X on landing or article)
  useEffect(() => {
    // First: capture any UTM/ref/utm_source on this URL into localStorage so it survives nav
    captureSignupSource();
    // Also capture ?ref=CODE so the register endpoint can apply the 30-day trial
    captureRefFromUrl();

    const stored = getStoredPromo();
    if (stored && !registerData.discountCode) {
      setRegisterData(prev => ({ ...prev, discountCode: stored }));
      // Validate immediately so the green checkmark shows
      api.post('/auth/validate-discount', { code: stored })
        .then(res => {
          const d = res.data;
          const label = d.type === 'extended_trial'
            ? `${d.trial_days}-day free trial (instead of 7)`
            : `${d.percent_off}% off ${d.type === 'first_month' ? 'first month' : d.type === 'first_year' ? 'first year' : 'every renewal'}`;
          setDiscountStatus({ ok: true, summary: label });
        })
        .catch(() => setDiscountStatus(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recovery flow state
  const [recoveryMode, setRecoveryMode] = useState(null); // null | 'username' | 'password'
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoveredUsername, setRecoveredUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleLookupPhone = async (mode) => {
    const digits = recoveryPhone.replace(/\D/g, '');
    if (digits.length < 7) { toast.error('Enter a valid phone number'); return; }
    try {
      setLoading(true);
      const res = await api.post('/auth/lookup-by-phone', { phone_number: digits });
      setRecoveredUsername(mode === 'username' ? res.data.username : res.data.masked_username);
      if (mode === 'password') setRecoveryMode('password-step2');
      else setRecoveryMode('username-found');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No account found');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 3) { toast.error('Password must be at least 3 characters'); return; }
    const digits = recoveryPhone.replace(/\D/g, '');
    try {
      setLoading(true);
      const res = await api.post('/auth/reset-password', { phone_number: digits, new_password: newPassword });
      setRecoveredUsername(res.data.username);
      setResetDone(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  const exitRecovery = () => {
    setRecoveryMode(null);
    setRecoveryPhone('');
    setRecoveredUsername('');
    setNewPassword('');
    setResetDone(false);
  };

  const handleRedirectAfterAuth = () => {
    // Redirect is handled by useEffect watching isAuthenticated
    // Just show the toast
    if (intendedRole === 'parent') {
      toast.success('Welcome! Browse teams and watch games.');
    } else {
      toast.success('Welcome, Coach!');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.username || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      if (rememberMe) localStorage.setItem('dmp-remember-username', loginData.username);
      else localStorage.removeItem('dmp-remember-username');
      const result = await login(loginData.username, loginData.password);
      setAuth(result.user, result.token);
      handleRedirectAfterAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.username || !registerData.password) {
      toast.error('Username and password are required');
      return;
    }
    // Validate phone format if provided (coach mode only and required for coaches per product decision)
    let phoneDigits = '';
    if (registerData.phone) {
      phoneDigits = registerData.phone.replace(/\D/g, '');
      if (phoneDigits && phoneDigits.length !== 10) {
        toast.error('Please enter a valid 10-digit phone number');
        return;
      }
    }
    if (isCoachMode && !phoneDigits) {
      toast.error('Phone number required for coach accounts');
      return;
    }
    // Validate email format if provided
    const email = registerData.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setRecoveryInfo(null);
      const result = await register(
        registerData.username,
        registerData.password,
        phoneDigits,
        intendedRole,
        email || null,
        registerData.discountCode.trim().toUpperCase() || null,
        getStoredSignupSource(),
        getStoredRefCode(),
      );
      setAuth(result.user, result.token);
      // Redirect happens in the useEffect above based on teams_as_coach length.
      // Show a slightly different toast for fresh coach accounts so the
      // onboarding redirect feels intentional.
      const isFreshCoach = intendedRole !== 'parent' && (!result.user?.teams_as_coach || result.user.teams_as_coach.length === 0);
      if (isFreshCoach) {
        toast.success('Welcome to Diamond Scorer! Let\'s get you scoring in 60 seconds.');
      } else {
        handleRedirectAfterAuth();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured backend response → show inline recovery card
      if (detail && typeof detail === 'object' && detail.code) {
        if (detail.code === 'email_taken' || detail.code === 'phone_taken') {
          setRecoveryInfo({
            field: detail.code === 'email_taken' ? 'email' : 'phone',
            existingUsername: detail.existing_username,
            hasSubscription: !!detail.has_active_subscription,
            suggestedPromo: detail.suggested_promo || 'WELCOMEBACK',
          });
        } else if (detail.code === 'email_required') {
          toast.error(detail.message);
        } else {
          toast.error(detail.message || 'Registration failed');
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateDiscount = async () => {
    const code = registerData.discountCode.trim().toUpperCase();
    if (!code) { setDiscountStatus(null); return; }
    try {
      const res = await api.post('/auth/validate-discount', { code });
      const d = res.data;
      const label = d.type === 'extended_trial'
        ? `${d.trial_days}-day trial`
        : `${d.percent_off}% off ${d.type === 'first_month' ? 'first month' : d.type === 'first_year' ? 'first year' : 'every renewal'}`;
      setDiscountStatus({ ok: true, summary: label });
    } catch (err) {
      setDiscountStatus({ ok: false, summary: err.response?.data?.detail || 'Invalid code' });
    }
  };

  const isCoachMode = intendedRole === 'coach';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: isCoachMode
          ? 'radial-gradient(ellipse at top, #131E36 0%, #0A1322 100%)'
          : 'radial-gradient(ellipse at top, #1A2542 0%, #0A1322 100%)',
      }}
      data-testid="login-page"
    >
      <div className="max-w-md w-full">
        <Button
          variant="ghost"
          className="text-white/70 hover:text-white mb-4"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center">
            {/* Official 3D DS monogram instead of a generic icon tile */}
            <img
              src="/diamond-scorer-logo.jpg"
              alt="Diamond Scorer"
              width={64}
              height={64}
              className="mx-auto mb-3"
              style={{
                borderRadius: 14,
                boxShadow: '0 0 20px rgba(46,190,107,0.5), 0 2px 8px rgba(0,0,0,0.25)',
                objectFit: 'cover',
              }}
            />
            <CardTitle 
              className="text-2xl"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                color: isCoachMode ? '#0E6B36' : '#B88C2E',
              }}
            >
              {isCoachMode ? 'COACH ACCESS' : 'PARENT ACCESS'}
            </CardTitle>
            <CardDescription>
              {isCoachMode 
                ? 'Login or register to manage your team'
                : 'Login or register to follow your player\'s games'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Recovery Mode UI */}
            {recoveryMode ? (
              <div className="space-y-4" data-testid="recovery-panel">
                <button
                  onClick={exitRecovery}
                  className="flex items-center text-sm text-stone-500 hover:text-stone-700 mb-2"
                  data-testid="recovery-back-btn"
                >
                  <ArrowLeft size={14} className="mr-1" /> Back to login
                </button>

                {/* Step 1: Choose recovery type */}
                {recoveryMode === 'choose' && (
                  <div className="space-y-3" data-testid="recovery-choose">
                    <p className="text-sm text-stone-600 text-center mb-4">
                      What do you need help with?
                    </p>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2 h-12"
                      onClick={() => setRecoveryMode('username')}
                      data-testid="recovery-forgot-username-btn"
                    >
                      <User size={16} /> I forgot my username
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2 h-12"
                      onClick={() => setRecoveryMode('password')}
                      data-testid="recovery-forgot-password-btn"
                    >
                      <KeyRound size={16} /> I forgot my password
                    </Button>
                  </div>
                )}

                {/* Step 2a: Phone entry for username lookup */}
                {recoveryMode === 'username' && (
                  <div className="space-y-4" data-testid="recovery-username-phone">
                    <p className="text-sm text-stone-600">
                      Enter the phone number you registered with to look up your username.
                    </p>
                    <div>
                      <Label htmlFor="recovery-phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <Input
                          id="recovery-phone"
                          data-testid="recovery-phone-input"
                          placeholder="(555) 555-5555"
                          value={recoveryPhone}
                          onChange={(e) => setRecoveryPhone(formatPhoneNumber(e.target.value))}
                          className="pl-10"
                          maxLength={14}
                          autoFocus
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => handleLookupPhone('username')}
                      disabled={loading}
                      data-testid="recovery-lookup-btn"
                      className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                      {loading ? 'Looking up...' : 'Find My Username'}
                    </Button>
                  </div>
                )}

                {/* Step 2a result: Username found */}
                {recoveryMode === 'username-found' && (
                  <div className="space-y-4 text-center" data-testid="recovery-username-result">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto ${
                      isCoachMode ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      <User className={isCoachMode ? 'text-green-700' : 'text-amber-600'} size={24} />
                    </div>
                    <p className="text-sm text-stone-600">Your username is:</p>
                    <p className="text-xl font-bold text-stone-900" data-testid="recovered-username">
                      {recoveredUsername}
                    </p>
                    <Button
                      onClick={exitRecovery}
                      data-testid="recovery-back-to-login-btn"
                      className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                      Back to Login
                    </Button>
                  </div>
                )}

                {/* Step 2b: Phone entry for password reset */}
                {recoveryMode === 'password' && (
                  <div className="space-y-4" data-testid="recovery-password-phone">
                    <p className="text-sm text-stone-600">
                      Enter the phone number you registered with to reset your password.
                    </p>
                    <div>
                      <Label htmlFor="recovery-phone-pw">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <Input
                          id="recovery-phone-pw"
                          data-testid="recovery-phone-input"
                          placeholder="(555) 555-5555"
                          value={recoveryPhone}
                          onChange={(e) => setRecoveryPhone(formatPhoneNumber(e.target.value))}
                          className="pl-10"
                          maxLength={14}
                          autoFocus
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => handleLookupPhone('password')}
                      disabled={loading}
                      data-testid="recovery-lookup-btn"
                      className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                      {loading ? 'Looking up...' : 'Verify Phone Number'}
                    </Button>
                  </div>
                )}

                {/* Step 3: Set new password */}
                {recoveryMode === 'password-step2' && !resetDone && (
                  <div className="space-y-4" data-testid="recovery-new-password">
                    <p className="text-sm text-stone-600">
                      Account found: <span className="font-semibold">{recoveredUsername}</span>
                    </p>
                    <p className="text-sm text-stone-500">Enter your new password below.</p>
                    <div>
                      <Label htmlFor="new-pw">New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <Input
                          id="new-pw"
                          type={showResetPw ? "text" : "password"}
                          data-testid="recovery-new-password-input"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10 pr-10"
                          autoFocus
                        />
                        <button type="button" onClick={() => setShowResetPw(!showResetPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                          data-testid="recovery-toggle-pw" tabIndex={-1}>
                          {showResetPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <Button
                      onClick={handleResetPassword}
                      disabled={loading}
                      data-testid="recovery-reset-btn"
                      className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </div>
                )}

                {/* Step 4: Password reset success */}
                {resetDone && (
                  <div className="space-y-4 text-center" data-testid="recovery-success">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto ${
                      isCoachMode ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      <KeyRound className={isCoachMode ? 'text-green-700' : 'text-amber-600'} size={24} />
                    </div>
                    <p className="text-sm text-stone-600">Password reset for:</p>
                    <p className="text-xl font-bold text-stone-900" data-testid="reset-username">
                      {recoveredUsername}
                    </p>
                    <p className="text-sm text-green-600 font-medium">You can now log in with your new password.</p>
                    <Button
                      onClick={exitRecovery}
                      data-testid="recovery-done-login-btn"
                      className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                      Back to Login
                    </Button>
                  </div>
                )}
              </div>
            ) : (
            /* Normal Login / Register Tabs */
            <Tabs value={authMode} onValueChange={setAuthMode}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-username">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="login-username"
                        data-testid="login-username"
                        placeholder="Enter username"
                        value={loginData.username}
                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="login-password"
                        type={showLoginPw ? "text" : "password"}
                        data-testid="login-password"
                        placeholder="Enter password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10"
                      />
                      <button type="button" onClick={() => setShowLoginPw(!showLoginPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        data-testid="login-toggle-pw" tabIndex={-1}>
                        {showLoginPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="remember-me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="rounded border-stone-300 accent-green-800"
                      data-testid="remember-me-checkbox"
                    />
                    <Label htmlFor="remember-me" className="text-sm text-stone-500 cursor-pointer font-normal">
                      Remember my username
                    </Label>
                  </div>
                  <Button 
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setRecoveryMode('choose')}
                    className={`w-full text-center text-sm mt-1 ${
                      isCoachMode ? 'text-green-700 hover:text-green-900' : 'text-amber-600 hover:text-amber-800'
                    }`}
                    data-testid="forgot-credentials-link"
                  >
                    Forgot username or password?
                  </button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-username">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="reg-username"
                        data-testid="register-username"
                        placeholder="Choose a username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="reg-password"
                        type={showRegPw ? "text" : "password"}
                        data-testid="register-password"
                        placeholder="Create a password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 pr-10"
                      />
                      <button type="button" onClick={() => setShowRegPw(!showRegPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        data-testid="register-toggle-pw" tabIndex={-1}>
                        {showRegPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reg-phone">Phone Number {!isCoachMode && <span className="text-stone-400 text-xs font-normal">(optional)</span>}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="reg-phone"
                        data-testid="register-phone"
                        placeholder="(555) 555-5555"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: formatPhoneNumber(e.target.value) })}
                        className="pl-10"
                        maxLength={14}
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      {isCoachMode 
                        ? 'Required for team access + SMS recovery'
                        : 'Optional — only needed if a coach has added you to a team'
                      }
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email <span className="text-red-500 text-xs font-bold">*</span></Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      <Input
                        id="reg-email"
                        type="email"
                        required
                        data-testid="register-email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">For password recovery + important account updates</p>
                  </div>
                  <div>
                    <Label htmlFor="reg-discount">Promo Code <span className="text-stone-400 text-xs font-normal">(optional)</span></Label>
                    <div className="relative flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <Input
                          id="reg-discount"
                          data-testid="register-discount"
                          placeholder="LAUNCH50"
                          value={registerData.discountCode}
                          onChange={(e) => {
                            setRegisterData({ ...registerData, discountCode: e.target.value.toUpperCase() });
                            setDiscountStatus(null);
                          }}
                          className={cn(
                            "pl-10 uppercase tracking-wider",
                            discountStatus?.ok && "border-emerald-500 bg-emerald-50",
                            discountStatus?.ok === false && "border-red-400 bg-red-50"
                          )}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={validateDiscount}
                        disabled={!registerData.discountCode.trim()}
                        data-testid="register-discount-apply"
                        className="shrink-0"
                      >
                        Apply
                      </Button>
                    </div>
                    {discountStatus && (
                      <p
                        className={cn(
                          "text-xs mt-1 flex items-center gap-1",
                          discountStatus.ok ? "text-emerald-700 font-semibold" : "text-red-600"
                        )}
                        data-testid="register-discount-status"
                      >
                        {discountStatus.ok && <Check size={12} />}
                        {discountStatus.summary}
                      </p>
                    )}
                  </div>

                  {/* Recovery card — shows when email/phone already exists on another account */}
                  {recoveryInfo && (
                    <div
                      className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 space-y-3"
                      data-testid="recovery-card"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-2xl shrink-0">👋</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-stone-900 text-sm">
                            Welcome back!
                          </p>
                          <p className="text-xs text-stone-700 mt-0.5">
                            We see {recoveryInfo.field === 'email' ? 'your email' : 'this phone number'} is already on an account
                            {recoveryInfo.existingUsername ? <> as <b>{recoveryInfo.existingUsername}</b></> : ''}. Sign in instead, or use the code below to come back at 50% off.
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                          onClick={() => {
                            // Switch to login tab and pre-fill username if we know it
                            if (recoveryInfo.existingUsername) {
                              setLoginData({ ...loginData, username: recoveryInfo.existingUsername });
                            }
                            setAuthMode('login');
                            setRecoveryInfo(null);
                          }}
                          data-testid="recovery-signin-btn"
                        >
                          Sign in instead
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-100 font-bold"
                          onClick={() => {
                            // Apply the recovery promo and auto-validate
                            setRegisterData((prev) => ({ ...prev, discountCode: recoveryInfo.suggestedPromo }));
                            setTimeout(validateDiscount, 100);
                            setRecoveryInfo(null);
                            toast.success(`Code ${recoveryInfo.suggestedPromo} applied — 50% off your first month`);
                          }}
                          data-testid="recovery-apply-promo-btn"
                        >
                          Use code {recoveryInfo.suggestedPromo} · 50% off
                        </Button>
                      </div>
                      <button
                        type="button"
                        className="text-[11px] text-stone-500 hover:text-stone-700 underline-offset-2 hover:underline w-full text-center"
                        onClick={() => setRecoveryInfo(null)}
                        data-testid="recovery-dismiss-btn"
                      >
                        Dismiss · I'll use different info
                      </button>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit"
                    className={`w-full ${isCoachMode ? 'bg-green-900 hover:bg-green-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-white/40 text-[10px] mt-4 leading-relaxed px-4">
          By signing in or creating an account, you agree to our{' '}
          <Link to="/terms" className="underline text-white/60 hover:text-white">Terms of Use</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline text-white/60 hover:text-white">Privacy Policy</Link>.
          This app uses your camera for lineup card scanning and sends push notifications for game updates.
        </p>

        <p className="text-center text-white/70 text-sm mt-3">
          {isCoachMode 
            ? <>Just a parent? <Link to="/login?role=parent" className="underline hover:text-white">Register as parent</Link></>
            : <>Are you a coach? <Link to="/login?role=coach" className="underline hover:text-white">Login as coach</Link></>
          }
        </p>
      </div>
    </div>
  );
}
